import { ArrowUpIcon, StopIcon, TrashIcon } from "@heroicons/react/16/solid";
import { useStore } from "@nanostores/react";
import { clsx } from "clsx";
import { ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import ScrollToBottom from "react-scroll-to-bottom";

import { useAssistantStream } from "../../hooks/useAssistantStream.ts";
import { useConversationHistory } from "../../hooks/useConversationHistory.ts";
import { useEphemeralDrafts } from "../../hooks/useEphemeralDraft.ts";
import { copilotQueryStream } from "../../services/copilot-streaming.ts";
import {
  $aiContextGroups,
  clearAIContext,
  getAllNodeIds,
  removeGroupFromAIContext,
  selectNodesInGraph,
  updateGroupName,
} from "../../stores/ai-context.ts";
import { AutoExpandingTextarea } from "../catalyst/AutoExpandingTextarea";
import { TEXTAREA_CONFIG } from "../catalyst/AutoExpandingTextarea.config";
import { Button } from "../catalyst/Button";
import { Text } from "../catalyst/Text";
import { LoadingIndicator } from "../LoadingIndicator.tsx";
import { UserMessage } from "../UserMessage";
import { AIAssistantMessage } from "./AIAssistantMessage";
import { AIContextGroupAttachment } from "./AIContextGroupAttachment.tsx";
import { ErrorRetryButton } from "./ErrorRetryButton.tsx";

export type AIAssistantChatSize = "large" | "small";

export interface AIAssistantChatBaseProps {
  attachments?: "aiContext" | "none";
  disclaimer?: ReactElement;
  emptyState?: ReactElement;
  followButtonClassName?: string;
  footerAboveInput?: ReactElement;
  getSendContext?: () => Record<string, unknown>;
  placeholder: string;
  size: AIAssistantChatSize;
}

export interface AIAssistantChatProps extends AIAssistantChatBaseProps {
  sessionId: string;
}

export const AIAssistantChat = ({
  attachments = "none",
  disclaimer,
  emptyState,
  followButtonClassName,
  footerAboveInput,
  getSendContext,
  placeholder,
  sessionId,
  size,
}: AIAssistantChatProps): ReactElement => {
  const aiContextGroups = useStore($aiContextGroups);
  const allNodeIds = useMemo(() => {
    return getAllNodeIds();
  }, [aiContextGroups]);

  const [inputValue, setInputValue] = useState("");

  const getSessionDrafts = useEphemeralDrafts(
    (state) => state.getSessionDrafts,
  );
  const ephemeralDrafts = getSessionDrafts(sessionId);

  const {
    error: conversationError,
    isLoading,
    loadConversation: loadConversationForSession,
    messages,
  } = useConversationHistory(sessionId);

  const {
    cancel,
    error: assistantStreamError,
    isStreaming,
    send,
  } = useAssistantStream({
    loadConversation: async () => {
      await loadConversationForSession();
    },
    sessionId: sessionId,
    startStream: async ({ abortController, events, input }) => {
      await copilotQueryStream(
        {
          context: input.context,
          message: input.userText,
          sessionId: input.sessionId,
        },
        {
          onChunk: (chunk) => events.onChunk?.(chunk),
          onComplete: (final) => events.onComplete?.(final),
          onError: (e) => events.onError?.(e),
        },
        { abortController },
      );
    },
  });

  const attachmentsEnabled = attachments === "aiContext";

  const submitMessage = useCallback(async (): Promise<void> => {
    const text = inputValue.trim();
    if (!text || isStreaming) return;

    try {
      setInputValue("");
      const contextFromCaller = getSendContext?.() ?? {};
      const context: Record<string, unknown> = { ...contextFromCaller };

      if (
        attachmentsEnabled &&
        context.selectedNodes == null &&
        allNodeIds.length > 0
      ) {
        context.selectedNodes = allNodeIds;
      }

      await send(text, context);
    } catch (error) {
      console.error("Failed to send message:", error);
    }
  }, [
    inputValue,
    isStreaming,
    sessionId,
    send,
    getSendContext,
    attachmentsEnabled,
    allNodeIds,
  ]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (inputValue.trim() && !isStreaming && sessionId) {
        void submitMessage();
      }
    }
  };

  const defaultEmptyState = (
    <div
      className={clsx("flex flex-col items-center justify-center text-center", {
        "gap-4 py-12": size === "large",
        "py-6": size === "small",
      })}
    >
      <div className={clsx({ "max-w-md": size === "large" })}>
        <Text
          className={clsx({
            "text-gray-400": size === "small",
            "text-zinc-600 dark:text-zinc-400": size === "large",
          })}
        >
          Where should we begin?
        </Text>
      </div>
    </div>
  );

  const followBtn =
    followButtonClassName ??
    (size === "large"
      ? "rounded-full shadow-lg bg-zinc-600 hover:bg-zinc-500 text-white"
      : "fixed bottom-20 right-4 z-10 h-10 w-10 rounded-full shadow-lg bg-zinc-700 hover:bg-zinc-600 text-white");

  const scrollClassName =
    size === "large"
      ? "h-full min-h-0 space-y-4 px-4 py-6 pb-8"
      : "h-full space-y-4 p-4 pb-8";

  const inputOuterClassName =
    size === "large"
      ? "shrink-0 border-t border-zinc-200 p-4 dark:border-zinc-700"
      : "border-t border-zinc-700 p-4";

  const handleGroupRemove = (groupId: string): void => {
    removeGroupFromAIContext(groupId);
  };

  const handleGroupRename = (groupId: string, newName: string): void => {
    updateGroupName(groupId, newName);
  };

  const handleGroupSelect = (nodeIds: string[]): void => {
    void selectNodesInGraph(nodeIds);
  };

  const attachmentsUi =
    attachmentsEnabled && aiContextGroups.length > 0 ? (
      <div>
        <div className="mb-2 flex items-center justify-between">
          <Text className="text-sm font-medium" color="default">
            Attachments ({aiContextGroups.length})
          </Text>
          <Button color="zinc" onClick={() => clearAIContext()} size="xs">
            <TrashIcon className="h-3 w-3" data-slot="icon" />
            Clear All
          </Button>
        </div>
        <div
          className={clsx("space-y-2", {
            "max-h-32 overflow-y-auto pr-2": aiContextGroups.length > 3,
          })}
        >
          {aiContextGroups.map((group) => (
            <AIContextGroupAttachment
              group={group}
              key={group.id}
              onRemove={handleGroupRemove}
              onRename={handleGroupRename}
              onSelect={handleGroupSelect}
            />
          ))}
        </div>
      </div>
    ) : null;

  const renderMessageRow = (
    message: unknown,
    key: number | string,
  ): ReactElement => {
    const m = message as { content?: string; role?: string };
    if (m.role === "user") {
      const user = <UserMessage message={m.content ?? ""} />;
      return (
        <div
          className={clsx({ "flex justify-end": size === "large" })}
          key={key}
        >
          {user}
        </div>
      );
    }
    const assistant = <AIAssistantMessage message={message as never} />;
    return (
      <div
        className={clsx({ "flex justify-start": size === "large" })}
        key={key}
      >
        {assistant}
      </div>
    );
  };

  const streamRow =
    isLoading || isStreaming ? (
      <div
        className={clsx({
          "flex items-center gap-2 py-4 text-sm text-zinc-500 dark:text-zinc-400":
            size === "large",
          "rounded-lg p-4": size === "small",
        })}
      >
        <div
          className={clsx(
            "h-4 w-4 animate-spin rounded-full border-2 border-t-transparent",
            size === "large" ? "border-zinc-400" : "border-gray-400",
          )}
        />
        <span
          className={clsx(
            size === "small" ? "text-sm text-gray-400" : undefined,
          )}
        >
          Thinking…
        </span>
      </div>
    ) : null;

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="min-h-0 flex-1 overflow-hidden">
          <ScrollToBottom
            className={scrollClassName}
            followButtonClassName={followBtn}
            initialScrollBehavior="smooth"
            mode="bottom"
          >
            {conversationError ? (
              <ErrorRetryButton
                error={conversationError}
                isLoading={isLoading}
                onRetry={() => void loadConversationForSession()}
                size={size}
              />
            ) : messages.length === 0 && ephemeralDrafts.length === 0 ? (
              (emptyState ?? defaultEmptyState)
            ) : (
              <div
                className={clsx("space-y-6", {
                  "mx-auto max-w-3xl": size === "large",
                  "space-y-4": size === "small",
                })}
              >
                {messages.map((m, idx) => {
                  const key =
                    (m as { messageId?: number | string }).messageId ??
                    (m as { createdAt?: number | string }).createdAt ??
                    idx;
                  return renderMessageRow(m, key);
                })}
                {ephemeralDrafts.map((d, idx) => {
                  const key =
                    (d as { messageId?: number | string }).messageId ??
                    `draft-${idx}`;
                  return renderMessageRow(d, key);
                })}
                {assistantStreamError ? (
                  <div className="space-y-4 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
                    <div className="text-sm font-medium text-red-700 dark:text-red-400">
                      Error
                    </div>
                    <p className="text-sm text-red-600 dark:text-red-300">
                      {assistantStreamError.message ||
                        "An unexpected error occurred while streaming the response."}
                    </p>
                  </div>
                ) : null}
              </div>
            )}

            {streamRow}

            {disclaimer ? (
              <div className="pt-4">{disclaimer}</div>
            ) : (
              <div className="pt-4 text-center text-xs text-gray-500">
                Miru Copilot can make mistakes. Please double-check responses
              </div>
            )}
          </ScrollToBottom>
        </div>
      </div>

      <div className={inputOuterClassName}>
        {attachmentsUi ? <div className="mb-2">{attachmentsUi}</div> : null}
        {footerAboveInput ? (
          <div className="mb-2">{footerAboveInput}</div>
        ) : null}

        <div
          className={clsx("relative", {
            "mx-auto max-w-3xl": size === "large",
            "w-full": size === "small",
          })}
        >
          <AutoExpandingTextarea
            className="w-full resize-none rounded-lg border border-zinc-300 bg-white py-3 pr-12 text-zinc-900 placeholder-zinc-500 focus:border-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-400 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100 dark:placeholder-zinc-400 dark:focus:border-zinc-500 dark:focus:ring-zinc-500"
            disabled={isStreaming}
            maxLines={TEXTAREA_CONFIG.maxLines}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            rows={1}
            value={inputValue}
          />

          <div
            className={clsx("absolute bottom-2 right-2", {
              "z-10": size === "small",
            })}
          >
            {isStreaming ? (
              <Button
                color="red"
                onClick={cancel}
                size="sm"
                title="Stop generating"
              >
                <StopIcon className="h-5 w-5" />
              </Button>
            ) : (
              <Button
                color="emerald"
                disabled={!inputValue.trim() || isStreaming}
                onClick={() => void submitMessage()}
                title="Send"
              >
                <ArrowUpIcon className="h-5 w-5" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export interface AIAssistantChatSkeletonProps extends AIAssistantChatBaseProps {
  createConversationHandler: () => Promise<{ sessionId?: string }>;
  header?: ReactElement;
  sessionId: null | string;
}

export const AIAssistantChatSkeleton = (
  props: AIAssistantChatSkeletonProps,
): ReactElement => {
  const { header, sessionId } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [createError, setCreateError] = useState<string>("");

  const ensureSessionExists = async (): Promise<void> => {
    if (sessionId) return;
    try {
      setIsLoading(true);
      setCreateError("");
      const result = await props.createConversationHandler();
      const newSessionId = result.sessionId;
      if (!newSessionId) {
        throw new Error("Failed to create conversation");
      }
    } catch (error) {
      console.error("Failed to create conversation:", error);
      const body = error as { message?: string } | null | undefined;
      setCreateError(
        body?.message?.trim() ??
          "Unknown error occurred while creating conversation. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) return;
    void ensureSessionExists();
  }, [sessionId]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      {header ? <div className="shrink-0">{header}</div> : null}
      {sessionId ? (
        <AIAssistantChat {...props} sessionId={sessionId} />
      ) : isLoading ? (
        <LoadingIndicator />
      ) : createError ? (
        <ErrorRetryButton
          error={createError}
          isLoading={isLoading}
          onRetry={ensureSessionExists}
          size="small"
        />
      ) : null}
    </div>
  );
};
